import {
  Box,
  Heading,
  Text,
  VStack,
  UnorderedList,
  OrderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';

export const PrivacyPolicyPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize="15px"
      lineHeight="24px">
      <VStack align="start" spacing={16}>
        <Box>
          <Heading as="h1" fontSize="40px" mb={4} fontFamily="marketingPage">
            Privacy Policy
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Last updated: November 12, 2025
          </Text>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            1. Controller
          </Heading>
          <VStack align="start" spacing={1}>
            <Text>
              <strong>Name:</strong> Merkatus.io Oy
            </Text>
            <Text>
              <strong>Business ID:</strong> 3541433-4
            </Text>
            <Text>
              <strong>Address:</strong> Lapinlahdenkatu 16, 00180 Helsinki,
              Finland
            </Text>
            <Text>
              <strong>Email:</strong>{' '}
              <Link
                href="mailto:support@merkatus.io"
                color="darkBlue.900"
                fontWeight="semibold"
                textDecoration="underline">
                support@merkatus.io
              </Link>
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            2. Legal Basis and Purposes of Processing
          </Heading>
          <Text mb={4}>
            The processing of personal data is based on the following purposes
            and legal bases:
          </Text>
          <OrderedList pl={4} spacing={4}>
            <ListItem>
              <Text fontWeight="semibold">
                Service Provision and User Authentication
              </Text>
              <Text mt={1}>
                <strong>Purpose:</strong> We process personal data to create and
                manage user accounts, authenticate users, provide access to our
                marketing intelligence platform, and deliver contracted
                services.
              </Text>
              <Text mt={1}>
                <strong>Legal Basis:</strong> Article 6(1)(b) GDPR – Processing
                is necessary for contract performance.
              </Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="semibold">Security and Access Control</Text>
              <Text mt={1}>
                <strong>Purpose:</strong> We process personal data to maintain
                platform security, prevent unauthorized access, monitor system
                usage, and ensure service integrity.
              </Text>
              <Text mt={1}>
                <strong>Legal Basis:</strong> Article 6(1)(f) GDPR – Processing
                is necessary for our legitimate interests in maintaining
                platform security and operational integrity.
              </Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="semibold">
                Customer Support and Service Communications
              </Text>
              <Text mt={1}>
                <strong>Purpose:</strong> We process personal data to provide
                technical support, service notifications, account management,
                and customer relationship management.
              </Text>
              <Text mt={1}>
                <strong>Legal Basis:</strong> Article 6(1)(b) GDPR – Processing
                is necessary for contract performance, and Article 6(1)(f) GDPR
                – Processing is necessary for our legitimate interests in
                providing effective customer service.
              </Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="semibold">
                Website Analytics, Cookies, User Experience
              </Text>
              <Text mt={1}>
                <strong>Purpose:</strong> We process personal data for analysing
                website usage and optimizing platform performance. We use
                cookies and similar tracking technologies to ensure website
                functionality, analyse usage patterns, and improve user
                experience. Our cookie policy is attached to this Privacy Notice
                as Annex 1.{' '}
                <Link
                  href="/cookie-policy"
                  color="darkBlue.900"
                  textDecoration="underline">
                  Cookie Policy
                </Link>
              </Text>

              <Text mt={1}>
                <strong>Legal Basis:</strong> Article 6(1)(b) GDPR for contract
                performance, and Article 6(1)(f) GDPR based on our legitimate
                interests in improving services and website functionality.
              </Text>
            </ListItem>
            <ListItem>
              <Text fontWeight="semibold">
                Data Processing Services (Corporate Customers as data controller
                and Merkatus as data processor)
              </Text>
              <Text mt={1}>
                <strong>Purpose:</strong> We process personal data and
                aggregated, anonymized business data provided by Corporate
                Customers solely to provide the contracted marketing
                intelligence services as instructed by such Corporate Customers.
              </Text>
              <Text mt={1}>
                <strong>Legal Basis:</strong> Processing is conducted on behalf
                of the Corporate Customer acting as data controller. The legal
                basis is determined by the Corporate Customer in accordance with
                their own privacy policy and data protection obligations.
              </Text>
            </ListItem>
          </OrderedList>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            3. Automated Decisions
          </Heading>
          <VStack align="start" spacing={3}>
            <Text>
              Automated decision-making takes place when an electronic system
              uses personal information to make a decision without human
              intervention. You will not be subject to decisions that have a
              significant impact on you based solely on automated
              decision-making unless we have a lawful basis for doing so and we
              have notified you. We do not intend to make decisions about data
              subjects through automated decision-making.
            </Text>
            <Text>
              If we make an automated decision on the basis of any particularly
              sensitive personal information, we must have either your explicit
              written consent or it must be justified in the public interest,
              and we must also put in place appropriate measures to safeguard
              your rights.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            4. Personal Data Collected
          </Heading>
          <Text mb={4}>
            Personal data collection is necessary to provide our contracted
            services, maintain platform security, ensure proper user
            authentication, fulfill our legal obligations, and provide effective
            customer support. Without providing the required personal data, we
            cannot create your account, deliver our services, process
            subscriptions, or provide technical support and service
            communications. Optional data collection will be clearly indicated,
            and you may choose not to provide such information without affecting
            core service functionality. We do not use personal data for
            modelling purposes.
          </Text>
          <Text mb={2}>
            The following information of the data subjects may be stored and
            processed:
          </Text>
          <UnorderedList pl={4} spacing={3}>
            <ListItem>
              Personal identification information: name, email, phone number,
              job title, IP address, usage logs.
            </ListItem>
            <ListItem>
              In addition, the following data may be collected:
              <UnorderedList pl={4} spacing={2} mt={2}>
                <ListItem>
                  Information related to the processing of the data, such as the
                  date of collection and the source of the data.
                </ListItem>
                <ListItem>
                  Necessary information related to the use of identification and
                  authentication tools and services.
                </ListItem>
                <ListItem>
                  Other information related to the purpose of the processing,
                  such as information that can be connected to the data subject
                  about the use of the website when using the service, such as
                  the user’s IP address, time of visit, pages visited, browser
                  type used, network address from which the user came to the
                  website and the server from which the user came to the
                  website.
                </ListItem>
              </UnorderedList>
            </ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            5. Retention Period of Personal Data
          </Heading>
          <VStack align="start" spacing={3}>
            <Text>
              <strong>Account and Authentication Data:</strong> Retained for the
              duration of the service agreement plus three months after
              termination for account closure and transition purposes.
            </Text>
            <Text>
              <strong>Usage Logs and Technical Data:</strong> Retained for the
              duration of the service agreement plus three months for security
              monitoring and service improvement purposes.
            </Text>
            <Text>
              <strong>Customer Support Communications:</strong> Retained for
              three years for service quality and dispute resolution purposes.
            </Text>
            <Text>
              <strong>Website Analytics and Cookie Data:</strong> Retained for
              the period set out in Annex 1.
            </Text>
            <Text>
              <strong>Data Processing Services:</strong> When Merkatus is acting
              as data processor, personal data will be deleted or returned to
              the Corporate Customer within 90 days of effective date of service
              termination, unless applicable law requires longer retention.
            </Text>
            <Text>
              <strong>Legal Retention Requirements:</strong> Where legal
              obligations require longer retention periods, data will be
              retained only for the minimum period required by applicable law.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            6. How Data Is Collected
          </Heading>
          <VStack align="start" spacing={3}>
            <Text>
              Data is primarily obtained from the following sources: directly
              from the data subject and/or information generated through the use
              of the service, cookies or website by the data subject.
            </Text>
            <Text>
              Data may also be obtained indirectly from the following sources:
              data subject’s employer, who is Merkatus’ Corporate Customer.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            7. Disclosure or Transfer of Data
          </Heading>
          <Text mb={4}>
            Processing tasks may be outsourced to external service providers in
            accordance with and within the limits set by data protection
            legislation. Main operators to whom processing tasks have been
            outsourced or personal data disclosed include:
          </Text>
          <UnorderedList pl={4} spacing={3}>
            <ListItem>
              Technical Service Providers (cloud hosting, IT infrastructure,
              software maintenance) – for service delivery, platform
              functionality, and technical support.
            </ListItem>
            <ListItem>
              Customer Support and Communication Providers (help desk software,
              communication platforms) – for customer service and technical
              assistance.
            </ListItem>
            <ListItem>
              Analytics and Security Service Providers (website analytics,
              cybersecurity firms, monitoring services) – for platform
              performance optimization, security incident response, and fraud
              prevention.
            </ListItem>
            <ListItem>
              Authorized Customer Personnel – for service delivery as instructed
              by the Corporate Customer acting as data controller.
            </ListItem>
            <ListItem>
              Government Authorities and Regulatory Bodies – when required by
              law, legal process, or regulatory compliance.
            </ListItem>
          </UnorderedList>
          <Text mt={4}>
            The data shall not be disclosed to other parties than the ones
            above, except in accordance with separate consent and/or
            regulations.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            8. Transfer of Data Outside the EU/EEA
          </Heading>
          <VStack align="start" spacing={3}>
            <Text>
              Personal data may be transferred outside the European Union or the
              European Economic Area in accordance with and within the limits
              set by data protection regulation.
            </Text>
            <Text>
              Personal data may be transferred to countries outside the EU/EEA
              only when adequate safeguards are in place, such as European
              Commission adequacy decisions, Standard Contractual Clauses (SCCs)
              approved by the European Commission, or other legally recognized
              transfer mechanisms. You may request copies of these safeguards by
              contacting us at{' '}
              <Link
                href="mailto:support@merkatus.io"
                color="darkBlue.900"
                fontWeight="semibold"
                textDecoration="underline">
                support@merkatus.io
              </Link>
              .
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            9. Security Measures
          </Heading>
          <Text>
            We implement reasonable security measures to protect data from
            unauthorized access, loss, or misuse, including secure servers and
            encryption, limited access to personal data, and regular security
            updates.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            10. Rights of Data Subject
          </Heading>
          <VStack align="start" spacing={3}>
            <Text>
              <strong>Right to access:</strong> Data subjects have the right to
              receive confirmation on whether or not the controller is
              processing personal data that concerns them. The controller must
              provide the data subject with a copy of the personal data being
              processed. As a rule, the exercise of rights is free of charge.
            </Text>
            <Text>
              <strong>Right to rectification:</strong> Data subjects have the
              right to demand the rectification of inaccurate personal data
              concerning them and to have incomplete personal data completed.
            </Text>
            <Text>
              <strong>Right to erasure:</strong> In certain cases, specified in
              Article 17 of the GDPR, the data subject has the right to have the
              controller erase data concerning them. For example, when the
              personal data is no longer necessary in relation to the purposes
              for which it was collected or otherwise processed.
            </Text>
            <Text>
              <strong>Right to restriction of processing:</strong> In certain
              cases, specified in Article 18 of the GDPR, the data subject can
              request the controller to restrict the processing of personal data
              concerning them. The right to restriction exists for example, when
              the data subject contests the accuracy of the personal data. In
              such cases, the processing will be restricted for a period
              enabling the controller to verify the accuracy of the personal
              data.
            </Text>
            <Text>
              <strong>Right to object:</strong> In certain situations, the data
              subject has the right to object to the processing of their
              personal data. If the data is processed for the performance of a
              task carried out for the purposes of the compelling legitimate
              interests pursued by the controller or a third party, the data
              subject has the right to object to the processing on grounds
              relating to their particular situation. In such cases, the
              processing must be stopped unless the controller demonstrates
              compelling legitimate grounds for the processing which override
              the interests, rights and freedoms of the data subject.
            </Text>
            <Text>
              <strong>Right to data portability:</strong> The data subject has
              the right to receive the personal data that they have provided to
              a controller in a structured, commonly used and machine-readable
              format and, if desired, transmit that data to another controller.
            </Text>
            <Text>
              <strong>Right to withdraw consent:</strong> If personal data is
              processed based on consent, the data subject has the right to
              withdraw consent by informing the controller. Withdrawal of
              consent does not affect the lawfulness of the processing carried
              out prior to the withdrawal of consent.
            </Text>
            <Text>
              <strong>
                Right to lodge a complaint with the supervisory authority:
              </strong>{' '}
              The data subject has the right to lodge a complaint with the
              supervisory authority if the data subject considers that the
              processing of personal data infringes the data protection
              regulation.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            11. Contact Information and the Use of Data Subject Rights
          </Heading>
          <Text>
            In all matters relating to the processing of personal data and in
            situations involving the exercise of rights, the data subject should
            contact the controller using the contact details provided at the
            beginning of this privacy notice.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            12. Changes to Privacy Notice
          </Heading>
          <Text>
            We can make changes to this privacy notice if the methods or
            purposes of the processing of personal data change. However, the
            content of the privacy notice should be reviewed on a regular basis.
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
